import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CallStatus, defaultUsedFilters, FilterBoolean, filters, FilterType, IDateTime, IFilter } from './company-call-history-filters.model';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as dayjsTimezone from 'dayjs/plugin/timezone';
import { AccountFacade } from '../../../account/store/facade';
import { filter, take } from 'rxjs/operators';
import { ICallEventSearchFilters, IPhoneCallTag, Pbx, PhoneCallTypes } from 'voxloud-types/dist/models';

@Component({
  selector: 'company-call-history-filters',
  templateUrl: './company-call-history-filters.component.html',
  styleUrls: ['./company-call-history-filters.component.scss']
})
export class CompanyCallHistoryFiltersComponent {

  @Input() set inputFilters(f: ICallEventSearchFilters) {
    if(!f) return;
    this._inputFilters = f;
    this.usedFilters = [...defaultUsedFilters];
    this.unusedFilters = [...filters];
    Object.keys(f).forEach((filterName: string) => {
      const isUsed = this.usedFilters.findIndex(ff => ff.name === filterName) != -1;
      const availableIdx = this.unusedFilters.findIndex(ff => ff.name === filterName);
      if(!isUsed && availableIdx != -1) {
        this.simpleAdd(availableIdx);
      }
    });
    if(this.pbx) {
      // if !pbx, then ngOnInit should still run and the form will be init there
      this.form = new FormGroup({});
      this.initForm();
    }
  }
  @Input() tags: IPhoneCallTag[] = [];

  @Output() selectedFilters: EventEmitter<ICallEventSearchFilters> = new EventEmitter();

  public usedFilters: IFilter[] = [...defaultUsedFilters];
  public unusedFilters: IFilter[] = [...filters];
  public FilterType = FilterType;
  public form: FormGroup = new FormGroup({});
  private pbx: Pbx;
  private _inputFilters: ICallEventSearchFilters;

  constructor(
    private accountFacade: AccountFacade
  ) { }

  ngOnInit() {
    this.accountFacade.pbx$.pipe(take(1)).subscribe({
      next: (pbx) => {
        this.pbx = pbx;
        this.initForm();
      }
    });
  }

  public simpleAdd(index: number) {
    const filter = this.unusedFilters[index];
    this.usedFilters = [...this.usedFilters, filter];
    this.unusedFilters.splice(index, 1);
  }

  public swapFilter(oldFilter: IFilter, newFilter: IFilter) {
    this.usedFilters = this.usedFilters.map(f => f === oldFilter ? newFilter : f);
    this.unusedFilters = this.unusedFilters.map(f => f === newFilter ? oldFilter : f);
    this.removeFormControl(oldFilter);
    this.addFormControl(newFilter);
  }

  public addFilter(filter: IFilter, value?: any) {
    switch(filter.type){
      case FilterType.DATE_TIME:
        this.addFormControl(filter, value || filter.default);
        break;
      case FilterType.TIMEZONE:
        this.addFormControl(filter, value || this.pbx.time_zone);
        break;
      default:
        this.addFormControl(filter, value);
    }
  }

  public addFirstFilter() {
    const filter = this.unusedFilters[0];
    this.usedFilters = [...this.usedFilters, this.unusedFilters[0]];
    this.unusedFilters = this.unusedFilters.slice(1);
    this.addFilter(filter);
  }

  public removeFilter(filter: IFilter) {
    this.usedFilters = this.usedFilters.filter(f => f !== filter);
    this.unusedFilters = [...this.unusedFilters, filter];
    this.removeFormControl(filter);
  }

  public formSubmit() {
    let dtrOutput: IDateTime[] = [];
    if(this.form.value.date_time_ranges) {
      dayjs.extend(dayjsTimezone);
      dayjs.extend(utc);
      this.form.value.date_time_ranges.forEach((dtr: IDateTime) => {
        let dtrTemp: IDateTime = {
          from: null,
          to: null,
          from_time: null,
          to_time: null
        };
        dtrTemp.from = `${dayjs.tz(dtr.from, this.form.value.timezone).format()}[${this.form.value.timezone}]`;
        dtrTemp.to = `${dayjs.tz(dtr.to, this.form.value.timezone).format()}[${this.form.value.timezone}]`;
        dtrTemp.from_time = dtr.from_time;
        dtrTemp.to_time = dtr.to_time;
        dtrOutput.push(dtrTemp);
      });
    }

    if(typeof this.form.value.event_types === 'string') {
      this.form.value.event_types = [this.form.value.event_types];
    }
    if(typeof this.form.value.directions === 'string') {
      this.form.value.directions = [this.form.value.directions];
    }
    if(typeof this.form.value.is_answered === 'string') {
      this.form.value.is_answered = this.form.value.is_answered === CallStatus.answered ? true : false;
    } else {
      this.form.value.is_answered = null;
    }
    
    if(typeof this.form.value.voicemail === 'string') {
      this.form.value.voicemail = this.form.value.voicemail === FilterBoolean.yes ? true : false;
    }
    if(typeof this.form.value.recorded === 'string') {
      this.form.value.recorded = this.form.value.recorded === FilterBoolean.yes ? true : false;
    }

    const output: ICallEventSearchFilters = {
      account_id: null,
      ...this.form.value,
      date_time_ranges: dtrOutput,
      event_types: (this.form.value.event_types && this.form.value.event_types.length > 0 && this.form.value.event_types) || null,
      directions: (this.form.value.directions && this.form.value.directions.length > 0 && this.form.value.directions) || null,
    }

    Object.keys(output).forEach(k => {
      if(output[k] == FilterBoolean.yes || output[k] == FilterBoolean.no) {
        output[k] = output[k] == FilterBoolean.yes ? true : false;
      }
      if(output[k] === null || output[k] === undefined || output[k].length === 0)
        delete output[k]
    });

    if(output.duration) {
      output.duration.from = output.duration.from * 60;
      output.duration.to = output.duration.to * 60;
    }

    this.selectedFilters.emit(output);
  }

  public formClear() {
    this.form = new UntypedFormGroup({});
    this.usedFilters = [...defaultUsedFilters];
    this.unusedFilters = [...filters];
    this.initDefaultForm();
  }

  private initForm() {
    this.usedFilters.forEach(filter => {
      if(this._inputFilters && (this._inputFilters[filter.name] !== null || this._inputFilters[filter.name] !== undefined)) {
        this.addFilter(filter, this._inputFilters[filter.name]);
      } else {
        this.addFilter(filter);
      }
    });
  }

  private initDefaultForm() {
    this.usedFilters.forEach(filter => {
      this.addFilter(filter);
    });
  }

  private addFormControl(filter: IFilter, value?: any) {
    if(filter.type === FilterType.DROPDOWN) {
      let v = value;
      if(value === null || value === undefined) {
        v = filter.options[0];
      }
      this.form.addControl(filter.name, new FormControl<any>(v));
    } else {
      this.form.addControl(filter.name, new FormControl<any>(value));
    }
  }

  private removeFormControl(filter: IFilter) {
    this.form.removeControl(filter.name);
  }
}
